import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { PriceSearchIcon } from '#/shared/ui/icons'

interface PriceCellProps {
  value?: number
  savedValue?: number
  previousValue?: number
  seedValue?: number
  onSave?: (value: number) => void
  isPending?: boolean
  onPriceIconClick?: () => void
  /**
   * Optional control rendered in the icon column beneath the price-search
   * icon, aligned with the Save button. The leads tariff cell injects the
   * market-rate "Check Rate" trigger here (shared/ui can't import features).
   */
  rateCheckSlot?: ReactNode
}

function formatPrice(value: number) {
  return value.toLocaleString('en-US')
}

function SavedPrice({
  value,
  previousValue,
}: {
  value: number
  previousValue?: number
}) {
  return (
    <div className="flex flex-col items-start">
      <div className="flex h-7 items-center">
        <span className="text-sm font-bold">
          <span className="text-primary">$</span>
          <span className="text-gray-900">{formatPrice(value)}</span>
        </span>
      </div>
      {previousValue != null && (
        <div className="flex h-7 items-center">
          <span className="text-sm text-gray-500">
            ${formatPrice(previousValue)}
          </span>
        </div>
      )}
    </div>
  )
}

function PriceEditor({
  value,
  seedValue,
  onSave,
  isPending,
  onPriceIconClick,
  rateCheckSlot,
}: {
  value?: number
  seedValue?: number
  onSave?: (value: number) => void
  isPending?: boolean
  onPriceIconClick?: () => void
  rateCheckSlot?: ReactNode
}) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? '')
  useEffect(() => {
    if (seedValue == null) return
    setInputValue(String(seedValue))
  }, [seedValue])
  const hasValue = inputValue.trim().length > 0
  const disabled = !hasValue || isPending

  return (
    // The inline editor is fully interactive; keep its clicks from bubbling to
    // a row-level onClick (which would open the detail view mid-edit).
    <div
      className="flex items-start gap-0.5"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Icon column: price-search aligns with the input, the rate-check slot
          aligns with the Save button below. */}
      <div className="flex flex-col gap-2">
        {onPriceIconClick ? (
          <button
            type="button"
            onClick={onPriceIconClick}
            aria-label={t('table.marketRate.open')}
            className="flex h-6 cursor-pointer items-center justify-center rounded hover:bg-gray-100"
          >
            <PriceSearchIcon className="size-3.5 text-orange-500" />
          </button>
        ) : (
          <div className="flex h-6 items-center justify-center">
            <PriceSearchIcon className="size-3.5 text-orange-500" />
          </div>
        )}
        {rateCheckSlot && (
          <div className="flex h-6 items-center justify-center">
            {rateCheckSlot}
          </div>
        )}
      </div>
      <div className="flex flex-col items-start gap-2">
        <input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isPending}
          className="h-6 w-15 appearance-none rounded-md border border-gray-200 px-1.5 text-sm font-medium text-gray-900 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
        />
        <button
          type="button"
          onClick={() => onSave?.(Number(inputValue))}
          disabled={disabled}
          className={cn(
            'flex h-6 w-15 items-center justify-center rounded-md text-sm font-medium',
            disabled
              ? 'cursor-not-allowed bg-gray-100 text-gray-500'
              : 'cursor-pointer bg-primary text-white hover:bg-primary/90',
          )}
        >
          {isPending ? t('table.saving') : t('table.save')}
        </button>
      </div>
    </div>
  )
}

export function PriceCell({
  value,
  savedValue,
  previousValue,
  seedValue,
  onSave,
  isPending,
  onPriceIconClick,
  rateCheckSlot,
}: PriceCellProps) {
  if (savedValue != null) {
    return <SavedPrice value={savedValue} previousValue={previousValue} />
  }
  return (
    <PriceEditor
      value={value}
      seedValue={seedValue}
      onSave={onSave}
      isPending={isPending}
      onPriceIconClick={onPriceIconClick}
      rateCheckSlot={rateCheckSlot}
    />
  )
}
